import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { Plus, Pencil, Trash2, Settings, Search, Wrench, AlertTriangle, Minus } from 'lucide-react';
import PageHeader from '../components/PageHeader';

const PRESET_COLORS = ['#818cf8','#f87171','#4ade80','#fb923c','#22d3ee','#facc15','#e879f9','#f472b6','#a78bfa','#34d399'];

// ── 카테고리 관리 모달 ──────────────────────────────────────────────
function CategoryModal({ categories, onClose, onRefresh }) {
  const [form, setForm] = useState({ name: '', color: '#818cf8' });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (!form.name.trim()) return;
    if (editing) await api.put(`/spare-parts/categories/${editing.id}`, form);
    else await api.post('/spare-parts/categories', form);
    setEditing(null);
    setForm({ name: '', color: '#818cf8' });
    onRefresh();
  };

  const remove = async (id) => {
    if (!confirm('삭제하시겠습니까? 해당 카테고리의 부품들은 미분류로 이동됩니다.')) return;
    await api.delete(`/spare-parts/categories/${id}`);
    onRefresh();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-sm">카테고리 관리</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-lg leading-none">×</button>
        </div>
        <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
          {categories.map(c => (
            <div key={c.id} className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
              <span className="text-white text-sm flex-1">{c.name}</span>
              <button onClick={() => { setEditing(c); setForm({ name: c.name, color: c.color }); }}
                className="text-gray-500 hover:text-white p-1 text-xs">수정</button>
              <button onClick={() => remove(c.id)} className="text-gray-500 hover:text-red-400 p-1"><Trash2 size={12} /></button>
            </div>
          ))}
          {categories.length === 0 && <p className="text-gray-600 text-xs text-center py-2">등록된 카테고리가 없습니다.</p>}
        </div>
        <div className="border-t border-gray-800 pt-3">
          <p className="text-gray-400 text-xs mb-2">{editing ? '카테고리 수정' : '카테고리 추가'}</p>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="카테고리 이름" onKeyDown={e => e.key === 'Enter' && save()}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-brand mb-2" />
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            {PRESET_COLORS.map(c => (
              <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                className={`w-5 h-5 rounded-full border-2 transition-transform ${form.color === c ? 'border-white scale-125' : 'border-transparent hover:scale-110'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="flex gap-2">
            {editing && (
              <button onClick={() => { setEditing(null); setForm({ name: '', color: '#818cf8' }); }}
                className="px-3 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
            )}
            <button onClick={save} className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">
              {editing ? '수정' : '추가'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 부품 등록/수정 모달 ─────────────────────────────────────────────
function PartFormModal({ part, categories, onSave, onClose }) {
  const [form, setForm] = useState(() => part || {
    name: '', categoryId: '', quantity: 0, unit: '개',
    minQuantity: 0, location: '', partNo: '', supplier: '', notes: '',
  });

  const handleSave = async () => {
    if (!form.name.trim()) return;
    if (!form.categoryId) { alert('카테고리를 선택해주세요.'); return; }
    const payload = { ...form, categoryId: form.categoryId || null };
    if (part?.id) await api.put(`/spare-parts/${part.id}`, payload);
    else await api.post('/spare-parts', payload);
    onSave();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-sm">{part?.id ? '부품 수정' : '부품 등록'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-lg leading-none">×</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-gray-400 text-xs mb-1 block">부품명 *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="부품명을 입력하세요"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">카테고리 <span className="text-brand">*</span></label>
              <select value={form.categoryId || ''} onChange={e => setForm(f => ({ ...f, categoryId: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand">
                <option value="" disabled>카테고리 선택</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">단위</label>
              <input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                placeholder="개"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">현재 재고</label>
              <input type="number" min={0} value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: +e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">최소 재고 기준</label>
              <input type="number" min={0} value={form.minQuantity} onChange={e => setForm(f => ({ ...f, minQuantity: +e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">보관 위치</label>
            <input value={form.location || ''} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              placeholder="예: A-3 선반 2단"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">부품 번호</label>
              <input value={form.partNo || ''} onChange={e => setForm(f => ({ ...f, partNo: e.target.value }))}
                placeholder="모델/품번"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">공급업체</label>
              <input value={form.supplier || ''} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))}
                placeholder="공급업체명"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">메모</label>
            <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="특이사항, 주문처, 규격 등"
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand resize-none" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 bg-gray-800 hover:bg-gray-700 text-white rounded-lg py-2 text-sm">취소</button>
          <button onClick={handleSave} className="flex-1 bg-brand hover:bg-brand/80 text-white rounded-lg py-2 text-sm font-medium">저장</button>
        </div>
      </div>
    </div>
  );
}

// ── 부품 카드 ───────────────────────────────────────────────────────
function PartCard({ part, onEdit, onDelete, onQtyChange }) {
  const isLow = part.minQuantity > 0 && part.quantity <= part.minQuantity;

  return (
    <div className={`bg-gray-900 border rounded-xl p-4 ${isLow ? 'border-yellow-500/40' : 'border-gray-800'}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            {part.category && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                style={{ backgroundColor: `${part.category.color}25`, color: part.category.color }}>
                {part.category.name}
              </span>
            )}
            {isLow && (
              <span className="flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded text-yellow-400 bg-yellow-400/15">
                <AlertTriangle size={9} /> 재고 부족
              </span>
            )}
          </div>
          <p className="text-white font-semibold text-sm truncate">{part.name}</p>
          {part.partNo && <p className="text-gray-600 text-xs">{part.partNo}</p>}
        </div>
        <div className="flex gap-0.5 ml-2">
          <button onClick={() => onEdit(part)} className="p-1.5 text-gray-600 hover:text-white rounded hover:bg-gray-800">
            <Pencil size={12} />
          </button>
          <button onClick={() => onDelete(part.id)} className="p-1.5 text-gray-600 hover:text-red-400 rounded hover:bg-gray-800">
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {/* 재고 수량 조절 */}
      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => onQtyChange(part.id, -1)}
          className="w-7 h-7 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white">
          <Minus size={12} />
        </button>
        <div className="flex-1 text-center">
          <span className={`text-xl font-bold ${isLow ? 'text-yellow-400' : 'text-white'}`}>{part.quantity}</span>
          <span className="text-gray-500 text-xs ml-1">{part.unit}</span>
        </div>
        <button onClick={() => onQtyChange(part.id, +1)}
          className="w-7 h-7 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white">
          <Plus size={12} />
        </button>
      </div>

      {/* 하단 정보 */}
      {(part.location || part.supplier) && (
        <div className="mt-2 pt-2 border-t border-gray-800 text-xs text-gray-600 space-y-0.5">
          {part.location && <p>📍 {part.location}</p>}
          {part.supplier && <p>🏭 {part.supplier}</p>}
        </div>
      )}
    </div>
  );
}

// ── 메인 ───────────────────────────────────────────────────────────
export default function SparePartsPage() {
  const [categories, setCategories] = useState([]);
  const [parts, setParts] = useState([]);
  const [selectedCatId, setSelectedCatId] = useState('all');
  const [search, setSearch] = useState('');
  const [showCatModal, setShowCatModal] = useState(false);
  const [editPart, setEditPart] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadCats = useCallback(() =>
    api.get('/spare-parts/categories').then(r => setCategories(r.data)), []);

  const loadParts = useCallback(() => {
    const params = {};
    if (selectedCatId !== 'all') params.categoryId = selectedCatId;
    if (search.trim()) params.search = search.trim();
    api.get('/spare-parts', { params }).then(r => setParts(r.data));
  }, [selectedCatId, search]);

  useEffect(() => { loadCats(); }, [loadCats]);
  useEffect(() => { loadParts(); }, [loadParts]);

  const handleDelete = async (id) => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/spare-parts/${id}`);
    loadParts();
  };

  const handleQtyChange = async (id, delta) => {
    await api.put(`/spare-parts/${id}/quantity`, { delta });
    loadParts();
  };

  const lowCount = useMemo(() => parts.filter(p => p.minQuantity > 0 && p.quantity <= p.minQuantity).length, [parts]);

  return (
    <div>
      <PageHeader icon={Wrench} title="수리부품 관리" count={parts.length}>
        <button onClick={() => setShowCatModal(true)}
          className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg px-3 py-2 text-sm">
          <Settings size={14} /> 카테고리
        </button>
        <button onClick={() => { setEditPart(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-brand hover:bg-brand/80 text-white rounded-lg px-4 py-2 text-sm font-medium">
          <Plus size={16} /> 부품 등록
        </button>
      </PageHeader>

      {/* 검색 */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="부품명, 부품번호, 공급업체로 검색..."
          className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600" />
      </div>

      {/* 카테고리 탭 */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        <button onClick={() => setSelectedCatId('all')}
          className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            selectedCatId === 'all' ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
          }`}>
          전체
        </button>
        {categories.map(c => (
          <button key={c.id} onClick={() => setSelectedCatId(String(c.id))}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              selectedCatId === String(c.id) ? 'text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
            style={selectedCatId === String(c.id) ? { backgroundColor: c.color } : {}}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
            {c.name}
          </button>
        ))}
      </div>

      {/* 부품 목록 */}
      {parts.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-16 text-center">
          <Wrench size={40} className="text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500">{search ? '검색 결과가 없습니다.' : '등록된 부품이 없습니다.'}</p>
          {!search && (
            <button onClick={() => { setEditPart(null); setShowForm(true); }}
              className="mt-3 text-brand text-sm hover:underline">
              첫 번째 부품 등록하기
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {parts.map(p => (
            <PartCard key={p.id} part={p}
              onEdit={(part) => { setEditPart(part); setShowForm(true); }}
              onDelete={handleDelete}
              onQtyChange={handleQtyChange}
            />
          ))}
        </div>
      )}

      {showCatModal && (
        <CategoryModal categories={categories} onClose={() => setShowCatModal(false)}
          onRefresh={() => { loadCats(); loadParts(); }} />
      )}

      {showForm && (
        <PartFormModal
          part={editPart}
          categories={categories}
          onSave={() => { loadParts(); setShowForm(false); setEditPart(null); }}
          onClose={() => { setShowForm(false); setEditPart(null); }}
        />
      )}
    </div>
  );
}
